import { useEffect, useState } from "react";
import { Box, styled } from "@mui/material";
import { Inventory2Outlined } from "@mui/icons-material";

interface EntityThumbProps {
  /** Resolved (signed) image URL, or null/undefined to show the fallback. */
  url?: string | null;
  /** Square edge length in px. Ignored when `fill` is set. */
  size?: number;
  /** Fill the parent (which controls dimensions/aspect) instead of a fixed square. */
  fill?: boolean;
  alt?: string;
}

/**
 * Thumbnail for an entity in a row/result/card: its primary photo when one
 * exists, otherwise the neutral box icon. Falls back to the icon if the image
 * fails to load (e.g. an expired signed URL), so a broken-image glyph never
 * shows. Presentational only — the caller resolves the URL. Pass `fill` to make
 * it stretch to the parent (e.g. a card's square image area).
 */
export const EntityThumb = ({
  url,
  size = 32,
  fill = false,
  alt = "",
}: EntityThumbProps) => {
  const [failed, setFailed] = useState(false);
  // A fresh URL (e.g. re-signed after expiry) is worth retrying.
  useEffect(() => setFailed(false), [url]);

  return (
    <Frame sx={fill ? { width: "100%", height: "100%" } : { width: size, height: size }}>
      {url && !failed ? (
        <Img
          src={url}
          alt={alt}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      ) : (
        <Inventory2Outlined fontSize={fill ? "large" : "small"} />
      )}
    </Frame>
  );
};

const Frame = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  borderRadius: 6,
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.action.hover,
  color: theme.palette.text.secondary,
}));

const Img = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
});
